import React, { useState, useCallback, useMemo } from 'react';
import { Box, Button, Typography } from '@lucid/lego';
import { downloadFileFromFilehub } from '../../../../../api/services/lupp/fileUpload/fileUpload';
import {
  mapToInitialFiles,
  groupFilesByCategory,
} from '../../../../../api/services/lupp/order/transformers.order';
import type { FilesByCategory } from '../../../../../api/services/lupp/order/transformers.order';
import { DOCUMENT_CATEGORY, FILEHUB_MODULE_NAME } from '../../../../../constants/constants';
import type { OrderDetailsResponse } from '../../../../../api/services/lupp/order/dto.order';
import CategoryUploadRow from './CategoryUploadRow';
import styles from './QualityCheck.module.scss';

/**
 * Props for the QualityCheck component
 * @property orderData - Order details containing order identifier and file references
 * @property theme - Visual theme for styling ('light' or 'dark')
 * @property labels - Localized labels for i18n support
 * @property dataTestId - Optional test ID for testing (defaults to 'final-quality-check')
 */
type QualityCheckProps = {
  orderData: OrderDetailsResponse;
  filesByCategory?: FilesByCategory;
  theme: 'light' | 'dark';
  labels: Record<string, string>;
  dataTestId?: string;
};

/**
 * Quality Check component
 * Displays quality check history and document upload functionality
 * Follows the same file upload/download pattern as Customer component
 *
 * @component
 * @param props - Component props
 * @param props.orderData - Order details containing order identifier and metadata
 * @param props.theme - Visual theme for the component ('light' or 'dark')
 * @param props.labels - Localized labels for UI text (i18n)
 * @param props.dataTestId - Optional test ID for testing (defaults to 'final-quality-check')
 * @returns Quality check component with file upload and document table
 * @example
 * ```tsx
 * <QualityCheck
 *   orderData={orderDetails}
 *   theme="dark"
 *   labels={labels}
 *   dataTestId="quality-check"
 * />
 * ```
 */
const QualityCheck: React.FC<QualityCheckProps> = ({
  orderData,
  filesByCategory,
  theme,
  labels,
  dataTestId = 'final-quality-check',
}) => {
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [extraSlots, setExtraSlots] = useState(0);

  // Use pre-grouped map from parent when available; compute as fallback
  const resolvedFilesByCategory = useMemo(
    () => filesByCategory ?? groupFilesByCategory(orderData.moduleFileReferenceDtoList),
    [filesByCategory, orderData.moduleFileReferenceDtoList]
  );

  // Transform files for display - filter by Quality Check category from order details
  const uploadedDocuments = useMemo(
    () => mapToInitialFiles(resolvedFilesByCategory[DOCUMENT_CATEGORY.FINAL_QUALITY_CHECK] ?? []),
    [resolvedFilesByCategory]
  );

  // Transform files for display - filter by FQC Other category for additional documents
  const additionalDocuments = useMemo(
    () =>
      mapToInitialFiles(resolvedFilesByCategory[DOCUMENT_CATEGORY.FINAL_QUALITY_CHECK_OTHER] ?? []),
    [resolvedFilesByCategory]
  );

  // Total additional rows: existing server files + user-added empty slots
  const additionalRowCount = additionalDocuments.length + extraSlots;

  // Document download handler using centralized utility
  const handleDocumentClick = useCallback(
    async (fileId: string, fileName: string) => {
      try {
        await downloadFileFromFilehub(fileId, fileName, FILEHUB_MODULE_NAME.ORDER_MANAGEMENT);
      } catch (error: unknown) {
        console.error('Failed to download file:', error);

        // Provide specific error messages based on error type
        let errorKey = 'download.error';
        if (error instanceof Error) {
          if (error.message.includes('URL not available')) {
            errorKey = 'download.urlNotAvailable';
          } else if (error.message.includes('Network') || error.message.includes('network')) {
            errorKey = 'download.networkError';
          }
        }

        setErrorMessage(labels[errorKey] || labels['download.error'] || '');
      }
    },
    [labels]
  );

  const handleAddDocument = useCallback(() => {
    setExtraSlots((prev) => prev + 1);
  }, []);

  const handleRemoveSlot = useCallback(() => {
    setExtraSlots((prev) => Math.max(prev - 1, 0));
  }, []);

  return (
    <Box className={styles.container} data-testid={dataTestId}>
      {/* Error Message */}
      {errorMessage && (
        <Box mb={16} pt={12} pb={12} pl={16} pr={16} className={styles.errorMessageText}>
          <Typography variant="body" size="medium" color="white" theme={theme}>
            {errorMessage}
          </Typography>
        </Box>
      )}
      {/* Section Title */}
      <Box mb={16}>
        <Typography variant="body" size="large" theme={theme} color="black">
          {labels?.['qualityCheck.relevantTitle'] ?? 'Relevant Document'}
        </Typography>
      </Box>

      {/* Final Quality Check — always present */}
      <CategoryUploadRow
        category={DOCUMENT_CATEGORY.FINAL_QUALITY_CHECK}
        orderData={orderData}
        initialFiles={uploadedDocuments}
        label={labels?.['qualityCheck.formLabel'] || 'Final Quality Check Form'}
        theme={theme}
        labels={labels}
        dataTestId={`${dataTestId}-upload`}
        onDocumentClick={handleDocumentClick}
        onError={setErrorMessage}
        removable
      />

      {/* Additional Documents — FQC Other category rows */}
      {Array.from({ length: additionalRowCount }, (_, index) => {
        const isUserAdded = index >= additionalDocuments.length;
        return (
          <Box key={`additional-${index}`}>
            <CategoryUploadRow
              category={DOCUMENT_CATEGORY.FINAL_QUALITY_CHECK_OTHER}
              orderData={orderData}
              initialFiles={index < additionalDocuments.length ? [additionalDocuments[index]] : []}
              label={
                labels?.['qualityCheck.fqcOtherLabel']
                  ? `${labels['qualityCheck.fqcOtherLabel']} ${index + 1}`
                  : `Final Quality Check - Other ${index + 1}`
              }
              theme={theme}
              labels={labels}
              dataTestId={`${dataTestId}-additional-upload-${index}`}
              onDocumentClick={handleDocumentClick}
              onError={setErrorMessage}
              onRemoveRow={isUserAdded ? handleRemoveSlot : undefined}
            />
          </Box>
        );
      })}

      {/* Add More Documents button */}
      <Box mt={16} className={styles.addDocumentRow}>
        <Button
          dataTestId={`${dataTestId}-add-document`}
          gaComponent="QualityCheck"
          color="neutral"
          theme={theme === 'dark' ? 'light' : 'dark'}
          label={labels?.['qualityCheck.addDocument'] || 'Add More Documents'}
          iconLeft={{ category: 'global', name: 'plus' }}
          handleClick={handleAddDocument}
        />
      </Box>
    </Box>
  );
};

QualityCheck.displayName = 'FinalQualityCheck';

export default QualityCheck;
